import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, XMarkIcon, MinusIcon } from '@heroicons/react/24/solid';

type Verdict = 'strong' | 'partial' | 'absent';

interface Cell {
  verdict: Verdict;
  note: string;
}

interface Competitor {
  key: string;
  name: string;
  highlight?: boolean;
}

const competitors: Competitor[] = [
  { key: 'lumen8', name: 'Lumen8', highlight: true },
  { key: 'mathspace', name: 'Mathspace' },
  { key: 'maths-online', name: 'Maths Online' },
  { key: 'education-perfect', name: 'Education Perfect' },
  { key: 'edrolo', name: 'Edrolo' },
  { key: 'cluey', name: 'Cluey Learning' },
  { key: 'tutero', name: 'Tutero' },
  { key: 'kinetic', name: 'Kinetic Education' },
];

const capabilities: Array<{
  title: string;
  description: string;
  cells: Record<string, Cell>;
}> = [
  {
    title: 'Curriculum-Aligned Content',
    description: 'Built around the Australian Curriculum',
    cells: {
      lumen8: { verdict: 'strong', note: 'Multi-subject AU' },
      mathspace: { verdict: 'strong', note: 'Math only' },
      'maths-online': { verdict: 'strong', note: 'Math only' },
      'education-perfect': { verdict: 'strong', note: 'Multi-subject' },
      edrolo: { verdict: 'strong', note: 'HSC/VCE core' },
      cluey: { verdict: 'strong', note: 'Math/Eng mapped' },
      tutero: { verdict: 'strong', note: 'Math, expanding' },
      kinetic: { verdict: 'strong', note: 'Math & English' },
    },
  },
  {
    title: 'Multi-Subject Adaptive Engine',
    description: 'Proprietary question engine, not a chatbot wrapper',
    cells: {
      lumen8: { verdict: 'strong', note: 'Proprietary engine' },
      mathspace: { verdict: 'partial', note: 'Single-session' },
      'maths-online': { verdict: 'absent', note: 'Static path' },
      'education-perfect': { verdict: 'partial', note: 'Linear branching' },
      edrolo: { verdict: 'absent', note: 'Linear progression' },
      cluey: { verdict: 'absent', note: 'Human dependent' },
      tutero: { verdict: 'partial', note: 'Platform diagnostics' },
      kinetic: { verdict: 'absent', note: 'Fixed progression' },
    },
  },
  {
    title: 'Multi-Year Mastery Tracking',
    description: 'Profile follows the student across years & schools',
    cells: {
      lumen8: { verdict: 'strong', note: 'Cross-year profile' },
      mathspace: { verdict: 'absent', note: 'Siloed by class' },
      'maths-online': { verdict: 'absent', note: 'Siloed by grade' },
      'education-perfect': { verdict: 'absent', note: 'Resets annually' },
      edrolo: { verdict: 'absent', note: 'Course specific' },
      cluey: { verdict: 'absent', note: 'Session-report based' },
      tutero: { verdict: 'absent', note: 'Session mapping' },
      kinetic: { verdict: 'absent', note: 'Siloed progression' },
    },
  },
  {
    title: 'Live Parent Dashboards',
    description: 'Continuous home visibility & engagement',
    cells: {
      lumen8: { verdict: 'strong', note: 'Continuous home view' },
      mathspace: { verdict: 'absent', note: 'School focus' },
      'maths-online': { verdict: 'partial', note: 'Weekly emails' },
      'education-perfect': { verdict: 'absent', note: 'School focus' },
      edrolo: { verdict: 'absent', note: 'School focus' },
      cluey: { verdict: 'partial', note: 'Post-session data' },
      tutero: { verdict: 'partial', note: 'Direct parent booking' },
      kinetic: { verdict: 'partial', note: 'Service desk check-ins' },
    },
  },
  {
    title: 'Teacher Admin Automation',
    description: 'Grading, lesson planning & differentiation',
    cells: {
      lumen8: { verdict: 'strong', note: 'AI-driven console' },
      mathspace: { verdict: 'partial', note: 'Auto-marking' },
      'maths-online': { verdict: 'absent', note: 'Consumer focus' },
      'education-perfect': { verdict: 'strong', note: 'Deep task tools' },
      edrolo: { verdict: 'partial', note: 'Markbook tracking' },
      cluey: { verdict: 'absent', note: 'Consumer focus' },
      tutero: { verdict: 'absent', note: 'Consumer focus' },
      kinetic: { verdict: 'absent', note: 'Consumer focus' },
    },
  },
  {
    title: 'Modern Student UX',
    description: 'Daily-use loop & rewards engine',
    cells: {
      lumen8: { verdict: 'strong', note: 'Daily-use rewards loop' },
      mathspace: { verdict: 'partial', note: 'Textbook style' },
      'maths-online': { verdict: 'absent', note: 'Legacy video UI' },
      'education-perfect': { verdict: 'partial', note: 'Slide-deck style' },
      edrolo: { verdict: 'partial', note: 'Standard video' },
      cluey: { verdict: 'partial', note: 'Whiteboard room' },
      tutero: { verdict: 'strong', note: 'Modern workspace' },
      kinetic: { verdict: 'absent', note: 'Legacy base' },
    },
  },
  {
    title: 'LMS/SIS Native Integration',
    description: 'School-stack interoperability, school-wide rollout',
    cells: {
      lumen8: { verdict: 'strong', note: 'Canvas / SEQTA-ready' },
      mathspace: { verdict: 'strong', note: 'LTI links' },
      'maths-online': { verdict: 'absent', note: 'Consumer focus' },
      'education-perfect': { verdict: 'strong', note: 'Canvas / SEQTA' },
      edrolo: { verdict: 'partial', note: 'Basic linking' },
      cluey: { verdict: 'absent', note: 'Pure B2C play' },
      tutero: { verdict: 'absent', note: 'Pure B2C play' },
      kinetic: { verdict: 'absent', note: 'Pure B2C play' },
    },
  },
  {
    title: 'Sovereign AI & Data Residency',
    description: 'Local residency, zero-retention compliance',
    cells: {
      lumen8: { verdict: 'strong', note: 'AU residency, zero-retention' },
      mathspace: { verdict: 'absent', note: 'Global cloud' },
      'maths-online': { verdict: 'absent', note: 'Legacy stack' },
      'education-perfect': { verdict: 'absent', note: 'Global cloud' },
      edrolo: { verdict: 'absent', note: 'Global cloud' },
      cluey: { verdict: 'absent', note: 'Offshore pools' },
      tutero: { verdict: 'absent', note: 'Commercial APIs' },
      kinetic: { verdict: 'absent', note: 'Legacy DB architecture' },
    },
  },
];

const VerdictMark: React.FC<{ verdict: Verdict }> = ({ verdict }) => {
  if (verdict === 'strong') return <CheckIcon className="w-4 h-4 text-[#10b981]" />;
  if (verdict === 'absent') return <XMarkIcon className="w-4 h-4 text-[#ef4444]" />;
  return <MinusIcon className="w-4 h-4 text-[#64748b]" />;
};

const IRSlideCompetitiveMatrix: React.FC = () => {
  const gridCols = 'grid-cols-[1.6fr_repeat(8,1fr)]';

  return (
    <div className="flex flex-col items-center justify-center h-full px-8 py-5">
      <motion.h2
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-4xl font-bold text-white mb-2 text-center"
      >
        Every rival owns a corner.{' '}
        <span className="bg-gradient-to-r from-[#0ea5e9] to-[#06b6d4] bg-clip-text text-transparent">
          Lumen8 owns the intersection.
        </span>
      </motion.h2>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-sm text-[#94a3b8] mb-4 text-center max-w-4xl"
      >
        Across the curriculum-adaptive and B2C tutoring landscape, no rival closes the full
        picture. Lumen8 is engineered for it.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="w-full max-w-7xl rounded-xl overflow-hidden"
        style={{
          background: 'rgba(15, 23, 42, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
        }}
      >
        {/* Header row */}
        <div className={`grid ${gridCols}`}>
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
            Capability
          </div>
          {competitors.map((c) => (
            <div
              key={c.key}
              className="px-2 py-2 text-center flex flex-col justify-center"
              style={
                c.highlight
                  ? {
                      background:
                        'linear-gradient(180deg, rgba(14, 165, 233, 0.28) 0%, rgba(6, 182, 212, 0.18) 100%)',
                      borderLeft: '1px solid rgba(14, 165, 233, 0.35)',
                      borderRight: '1px solid rgba(14, 165, 233, 0.35)',
                    }
                  : undefined
              }
            >
              <div className="text-xs font-bold leading-tight text-white">{c.name}</div>
            </div>
          ))}
        </div>

        {/* Capability rows */}
        {capabilities.map((cap, i) => (
          <div
            key={cap.title}
            className={`grid ${gridCols} border-t border-white/5`}
            style={{
              background: i % 2 === 1 ? 'rgba(30, 41, 59, 0.3)' : 'transparent',
            }}
          >
            <div className="px-3 py-2">
              <div className="text-xs font-semibold text-white leading-tight">{cap.title}</div>
              <div className="text-[10px] text-[#64748b] leading-snug mt-0.5">
                {cap.description}
              </div>
            </div>
            {competitors.map((c) => {
              const cell = cap.cells[c.key];
              return (
                <div
                  key={c.key}
                  className="px-1.5 py-2 flex flex-col items-center justify-center text-center"
                  style={
                    c.highlight
                      ? {
                          background: 'rgba(14, 165, 233, 0.08)',
                          borderLeft: '1px solid rgba(14, 165, 233, 0.3)',
                          borderRight: '1px solid rgba(14, 165, 233, 0.3)',
                        }
                      : undefined
                  }
                >
                  <VerdictMark verdict={cell.verdict} />
                  <span className="text-[10px] text-[#94a3b8] leading-tight mt-1">
                    {cell.note}
                  </span>
                </div>
              );
            })}
          </div>
        ))}
      </motion.div>

      {/* Legend + footnote */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="w-full max-w-7xl mt-3 flex items-center justify-between gap-6 flex-wrap"
      >
        <div className="flex items-center gap-5 text-xs text-[#94a3b8]">
          <div className="flex items-center gap-1.5">
            <CheckIcon className="w-4 h-4 text-[#10b981]" />
            <span>
              <span className="font-semibold text-white">Strong</span>
              <span className="text-[#64748b]"> · native capability</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <MinusIcon className="w-4 h-4 text-[#64748b]" />
            <span>
              <span className="font-semibold text-white">Partial</span>
              <span className="text-[#64748b]"> · limited or scripted</span>
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <XMarkIcon className="w-4 h-4 text-[#ef4444]" />
            <span>
              <span className="font-semibold text-white">Absent</span>
              <span className="text-[#64748b]"> · not addressed</span>
            </span>
          </div>
        </div>
        <div className="text-[10px] text-[#475569] max-w-md text-right">
          Assessment reflects Lumen8's review of publicly available product positioning as at
          May 2026.
        </div>
      </motion.div>
    </div>
  );
};

export default IRSlideCompetitiveMatrix;
